import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  User, Users, Building2, Phone, Mail, MapPin, 
  Plus, Trash2, ChevronDown, ChevronUp, Settings,
  Bell, BellOff, Shield
} from "lucide-react";
import { API } from "@/App";

const TITLES = ["Mr", "Mrs", "Ms", "Miss", "Dr", "Prof", "Sir", "Lady"];
const GENDERS = ["Male", "Female", "Prefer not to say"];
const CONSENT_METHODS = [
  "Online Form",
  "Verbal - Phone",
  "Verbal - In Person", 
  "Email Confirmation",
  "Written Letter",
  "Third Party Platform"
];
const FINANCE_STATUSES = [
  "Not Started",
  "AIP Obtained",
  "Full Application",
  "Valuation Booked",
  "Offer Issued",
  "Mortgage Complete",
  "Cash Buyer"
];
const CHAIN_STATUSES = [
  "No Chain",
  "Chain - All Proceeding",
  "Chain - Awaiting Info",
  "Chain - Issues",
  "Chain - Broken"
];

/**
 * Enhanced Applicant Form Component
 * Matches 10/90 system fields including:
 * - Multiple contacts (Mr & Mrs, partners, etc.)
 * - Business applicants
 * - Multiple phone numbers and emails
 * - Separate correspondence address
 * - Gender (for Dear Sir/Madam)
 * - Lead Staff assignment
 * - Finance & Chain Status with notes
 * - Branch selection
 * - Marketing Consents (GDPR)
 * - Reminder Exclusions
 */
export function ApplicantForm({ 
  value, 
  onChange, 
  label = "Applicant",
  showBusinessOption = true,
  showCorrespondenceAddress = true,
  showStatusFields = true,
  showMarketingConsents = true,
  showReminderExclusions = true,
  compact = false,
  staffList = [],
  branchList = []
}) {
  const [expandedContacts, setExpandedContacts] = useState([0]);
  const [expandedSections, setExpandedSections] = useState({
    contacts: true,
    address: true,
    status: false,
    marketing: false,
    reminders: false
  });
  const token = localStorage.getItem("token");
  const [localStaffList, setLocalStaffList] = useState(staffList);
  const [localBranchList, setLocalBranchList] = useState(branchList);

  // Fetch staff and branches if not provided
  useEffect(() => {
    const fetchData = async () => {
      if (staffList.length === 0) {
        try {
          const res = await axios.get(`${API}/admin/staff`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setLocalStaffList(res.data?.staff || []);
        } catch (e) {
          // Use default staff if API fails
          setLocalStaffList([
            { id: "jack", name: "Jack Goodacre" },
            { id: "josh", name: "Josh Saunders" },
            { id: "lauren", name: "Lauren Hughes" },
            { id: "loren", name: "Loren B" }
          ]);
        }
      }
      if (branchList.length === 0) {
        // Default branches based on 10/90 screenshots
        setLocalBranchList([
          { id: "kempston", name: "Kempston Office" },
          { id: "bedford", name: "Bedford Office" },
          { id: "cranfield", name: "Cranfield Office" }
        ]);
      }
    };
    fetchData();
  }, []);

  // Initialize default structure if empty
  const applicant = value || {
    is_business: false,
    business_name: "",
    contacts: [{
      title: "",
      first_name: "",
      last_name: "",
      friendly_name: "",
      gender: "",
      mobile_1: "",
      mobile_2: "",
      home_tel: "",
      work_tel: "",
      email_1: "",
      email_2: ""
    }],
    address: {
      line_1: "",
      line_2: "",
      town: "",
      county: "",
      postcode: "",
      country: "United Kingdom"
    },
    has_different_correspondence: false,
    correspondence_address: {
      line_1: "",
      line_2: "",
      town: "",
      county: "",
      postcode: "",
      country: "United Kingdom"
    },
    // Status fields
    lead_staff: "",
    finance_status: "",
    finance_status_notes: "",
    chain_status: "",
    chain_status_notes: "",
    branches: [],
    // Marketing consents
    consent_property_email: false,
    consent_property_sms: false,
    consent_marketing_email: false,
    consent_method: "",
    consent_notes: "",
    // Reminder exclusions
    exclude_viewing_email: false,
    exclude_viewing_sms: false,
    exclude_appointment_email: false,
    exclude_appointment_sms: false,
    notes: ""
  };

  const updateApplicant = (updates) => {
    onChange({ ...applicant, ...updates });
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...applicant.contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    updateApplicant({ contacts: newContacts });
  };

  const addContact = () => {
    const newContacts = [...applicant.contacts, {
      title: "",
      first_name: "",
      last_name: "",
      friendly_name: "",
      gender: "",
      mobile_1: "",
      mobile_2: "",
      home_tel: "",
      work_tel: "",
      email_1: "",
      email_2: ""
    }];
    updateApplicant({ contacts: newContacts });
    setExpandedContacts([...expandedContacts, newContacts.length - 1]);
  };

  const removeContact = (index) => {
    if (applicant.contacts.length <= 1) return;
    const newContacts = applicant.contacts.filter((_, i) => i !== index);
    updateApplicant({ contacts: newContacts });
    setExpandedContacts(expandedContacts.filter(i => i !== index).map(i => i > index ? i - 1 : i));
  };

  const toggleContactExpand = (index) => {
    if (expandedContacts.includes(index)) {
      setExpandedContacts(expandedContacts.filter(i => i !== index));
    } else {
      setExpandedContacts([...expandedContacts, index]);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const updateAddress = (field, value) => {
    updateApplicant({
      address: { ...applicant.address, [field]: value }
    });
  };

  const updateCorrespondenceAddress = (field, value) => {
    updateApplicant({
      correspondence_address: { ...applicant.correspondence_address, [field]: value }
    });
  };

  const toggleBranch = (branchId) => {
    const currentBranches = applicant.branches || [];
    if (currentBranches.includes(branchId)) {
      updateApplicant({ branches: currentBranches.filter(b => b !== branchId) });
    } else {
      updateApplicant({ branches: [...currentBranches, branchId] });
    }
  };

  return (
    <div className="space-y-4">
      {/* Business Option */}
      {showBusinessOption && (
        <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-2">
            <Checkbox
              id="is_business"
              checked={applicant.is_business}
              onCheckedChange={(checked) => updateApplicant({ is_business: checked })}
            />
            <Label htmlFor="is_business" className="cursor-pointer flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Business / Company
            </Label>
          </div>
          {applicant.is_business && (
            <Input
              placeholder="Business Name"
              value={applicant.business_name}
              onChange={(e) => updateApplicant({ business_name: e.target.value })}
              className="flex-1"
              data-testid="business-name-input"
            />
          )}
        </div>
      )}

      {/* Contacts Section */}
      <div className="space-y-3">
        <div 
          className="flex items-center justify-between cursor-pointer p-2 hover:bg-slate-50 rounded"
          onClick={() => toggleSection('contacts')}
        >
          <Label className="flex items-center gap-2 text-base font-medium cursor-pointer">
            <Users className="w-4 h-4" />
            {applicant.contacts.length > 1 ? "People" : "Person"} Details
            {expandedSections.contacts ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={(e) => { e.stopPropagation(); addContact(); }}
            data-testid="add-contact-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Person
          </Button>
        </div>

        {expandedSections.contacts && applicant.contacts.map((contact, index) => (
          <Card key={index} className="border-slate-200">
            <CardHeader className="py-3 px-4 cursor-pointer" onClick={() => toggleContactExpand(index)}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {contact.first_name || contact.last_name 
                    ? `${contact.title || ""} ${contact.first_name} ${contact.last_name}`.trim()
                    : `Person ${index + 1}`}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {applicant.contacts.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeContact(index);
                      }}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`remove-contact-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                  {expandedContacts.includes(index) 
                    ? <ChevronUp className="w-4 h-4 text-slate-400" />
                    : <ChevronDown className="w-4 h-4 text-slate-400" />
                  }
                </div>
              </div>
            </CardHeader>

            {expandedContacts.includes(index) && (
              <CardContent className="pt-0 px-4 pb-4 space-y-4">
                {/* Name Row */}
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-2">
                    <Label className="text-xs">Title</Label>
                    <Select
                      value={contact.title}
                      onValueChange={(v) => updateContact(index, "title", v)}
                    >
                      <SelectTrigger className="mt-1" data-testid={`title-select-${index}`}>
                        <SelectValue placeholder="--" />
                      </SelectTrigger>
                      <SelectContent>
                        {TITLES.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs">First Name *</Label>
                    <Input
                      value={contact.first_name}
                      onChange={(e) => updateContact(index, "first_name", e.target.value)}
                      className="mt-1"
                      data-testid={`first-name-${index}`}
                    />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs">Last Name *</Label>
                    <Input
                      value={contact.last_name}
                      onChange={(e) => updateContact(index, "last_name", e.target.value)}
                      className="mt-1"
                      data-testid={`last-name-${index}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Gender</Label>
                    <Select
                      value={contact.gender}
                      onValueChange={(v) => updateContact(index, "gender", v)}
                    >
                      <SelectTrigger className="mt-1" data-testid={`gender-select-${index}`}>
                        <SelectValue placeholder="--" />
                      </SelectTrigger>
                      <SelectContent>
                        {GENDERS.map(g => (
                          <SelectItem key={g} value={g}>{g}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Friendly Name */}
                <div>
                  <Label className="text-xs">Friendly Name <span className="text-slate-400">(used in salutation)</span></Label>
                  <Input
                    placeholder="e.g., Jack instead of John"
                    value={contact.friendly_name}
                    onChange={(e) => updateContact(index, "friendly_name", e.target.value)}
                    className="mt-1"
                    data-testid={`friendly-name-${index}`}
                  />
                </div>

                {/* Phone Numbers */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs flex items-center gap-1">
                      <Phone className="w-3 h-3" /> Mobile 1
                    </Label>
                    <Input
                      type="tel"
                      placeholder="07..."
                      value={contact.mobile_1}
                      onChange={(e) => updateContact(index, "mobile_1", e.target.value)}
                      className="mt-1"
                      data-testid={`mobile1-${index}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs flex items-center gap-1">
                      <Phone className="w-3 h-3" /> Mobile 2
                    </Label>
                    <Input
                      type="tel"
                      placeholder="07..."
                      value={contact.mobile_2}
                      onChange={(e) => updateContact(index, "mobile_2", e.target.value)}
                      className="mt-1"
                      data-testid={`mobile2-${index}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs flex items-center gap-1">
                      <Phone className="w-3 h-3" /> Home Tel
                    </Label>
                    <Input
                      type="tel"
                      placeholder="01234..."
                      value={contact.home_tel}
                      onChange={(e) => updateContact(index, "home_tel", e.target.value)}
                      className="mt-1"
                      data-testid={`home-tel-${index}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs flex items-center gap-1">
                      <Phone className="w-3 h-3" /> Work Tel
                    </Label>
                    <Input
                      type="tel"
                      placeholder="01234..."
                      value={contact.work_tel}
                      onChange={(e) => updateContact(index, "work_tel", e.target.value)}
                      className="mt-1"
                      data-testid={`work-tel-${index}`}
                    />
                  </div>
                </div>

                {/* Emails */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs flex items-center gap-1">
                      <Mail className="w-3 h-3" /> Email 1 (Primary)
                    </Label>
                    <Input
                      type="email"
                      placeholder="email@example.com"
                      value={contact.email_1}
                      onChange={(e) => updateContact(index, "email_1", e.target.value)}
                      className="mt-1"
                      data-testid={`email1-${index}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs flex items-center gap-1">
                      <Mail className="w-3 h-3" /> Email 2 (Secondary)
                    </Label>
                    <Input
                      type="email"
                      placeholder="email@example.com"
                      value={contact.email_2}
                      onChange={(e) => updateContact(index, "email_2", e.target.value)}
                      className="mt-1"
                      data-testid={`email2-${index}`}
                    />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* Main Address */}
      <div className="space-y-3">
        <div 
          className="flex items-center gap-2 cursor-pointer p-2 hover:bg-slate-50 rounded"
          onClick={() => toggleSection('address')}
        >
          <MapPin className="w-4 h-4" />
          <Label className="text-base font-medium cursor-pointer">Address</Label>
          {expandedSections.address ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
        
        {expandedSections.address && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs">Address Line 1 *</Label>
                <Input
                  placeholder="123 Main Street"
                  value={applicant.address?.line_1 || ""}
                  onChange={(e) => updateAddress("line_1", e.target.value)}
                  className="mt-1"
                  data-testid="address-line1"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Address Line 2</Label>
                <Input
                  placeholder="Apartment, suite, etc."
                  value={applicant.address?.line_2 || ""}
                  onChange={(e) => updateAddress("line_2", e.target.value)}
                  className="mt-1"
                  data-testid="address-line2"
                />
              </div>
              <div>
                <Label className="text-xs">Town / City *</Label>
                <Input
                  placeholder="Bedford"
                  value={applicant.address?.town || ""}
                  onChange={(e) => updateAddress("town", e.target.value)}
                  className="mt-1"
                  data-testid="address-town"
                />
              </div>
              <div>
                <Label className="text-xs">County</Label>
                <Input
                  placeholder="Bedfordshire"
                  value={applicant.address?.county || ""}
                  onChange={(e) => updateAddress("county", e.target.value)}
                  className="mt-1"
                  data-testid="address-county"
                />
              </div>
              <div>
                <Label className="text-xs">Postcode *</Label>
                <Input
                  placeholder="MK40 1AB"
                  value={applicant.address?.postcode || ""}
                  onChange={(e) => updateAddress("postcode", e.target.value.toUpperCase())}
                  className="mt-1"
                  data-testid="address-postcode"
                />
              </div>
              <div>
                <Label className="text-xs">Country *</Label>
                <Input
                  placeholder="United Kingdom"
                  value={applicant.address?.country || "United Kingdom"}
                  onChange={(e) => updateAddress("country", e.target.value)}
                  className="mt-1"
                  data-testid="address-country"
                />
              </div>
            </div>

            {/* Correspondence Address */}
            {showCorrespondenceAddress && (
              <div className="space-y-3 mt-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="different_correspondence"
                    checked={applicant.has_different_correspondence}
                    onCheckedChange={(checked) => updateApplicant({ has_different_correspondence: checked })}
                  />
                  <Label htmlFor="different_correspondence" className="cursor-pointer">
                    Different correspondence address
                  </Label>
                </div>

                {applicant.has_different_correspondence && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-slate-50 rounded-lg">
                    <div className="md:col-span-2">
                      <Label className="text-xs">Correspondence Address Line 1 *</Label>
                      <Input
                        placeholder="123 Other Street"
                        value={applicant.correspondence_address?.line_1 || ""}
                        onChange={(e) => updateCorrespondenceAddress("line_1", e.target.value)}
                        className="mt-1"
                        data-testid="corr-address-line1"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs">Correspondence Address Line 2</Label>
                      <Input
                        placeholder="Apartment, suite, etc."
                        value={applicant.correspondence_address?.line_2 || ""}
                        onChange={(e) => updateCorrespondenceAddress("line_2", e.target.value)}
                        className="mt-1"
                        data-testid="corr-address-line2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Town / City *</Label>
                      <Input
                        placeholder="London"
                        value={applicant.correspondence_address?.town || ""}
                        onChange={(e) => updateCorrespondenceAddress("town", e.target.value)}
                        className="mt-1"
                        data-testid="corr-address-town"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">County</Label>
                      <Input
                        placeholder="Greater London"
                        value={applicant.correspondence_address?.county || ""}
                        onChange={(e) => updateCorrespondenceAddress("county", e.target.value)}
                        className="mt-1"
                        data-testid="corr-address-county"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Postcode *</Label>
                      <Input
                        placeholder="SW1A 1AA"
                        value={applicant.correspondence_address?.postcode || ""}
                        onChange={(e) => updateCorrespondenceAddress("postcode", e.target.value.toUpperCase())}
                        className="mt-1"
                        data-testid="corr-address-postcode"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Country *</Label>
                      <Input
                        placeholder="United Kingdom"
                        value={applicant.correspondence_address?.country || "United Kingdom"}
                        onChange={(e) => updateCorrespondenceAddress("country", e.target.value)}
                        className="mt-1"
                        data-testid="corr-address-country"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Status Fields */}
      {showStatusFields && (
        <div className="space-y-3 border-t pt-4">
          <div 
            className="flex items-center gap-2 cursor-pointer p-2 hover:bg-slate-50 rounded"
            onClick={() => toggleSection('status')}
          >
            <Settings className="w-4 h-4" />
            <Label className="text-base font-medium cursor-pointer">Status & Assignment</Label>
            {expandedSections.status ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
          
          {expandedSections.status && (
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              {/* Lead Staff & Branch */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Lead Staff</Label>
                  <Select
                    value={applicant.lead_staff}
                    onValueChange={(v) => updateApplicant({ lead_staff: v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="lead-staff-select">
                      <SelectValue placeholder="Select staff member..." />
                    </SelectTrigger>
                    <SelectContent>
                      {localStaffList.map(s => (
                        <SelectItem key={s.id || s.name} value={s.id || s.name}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Branches</Label>
                  <div className="flex flex-wrap gap-3 mt-2">
                    {localBranchList.map(branch => (
                      <label key={branch.id} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={(applicant.branches || []).includes(branch.id)}
                          onCheckedChange={() => toggleBranch(branch.id)}
                        />
                        <span className="text-sm">{branch.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Finance Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Finance Status</Label>
                  <Select
                    value={applicant.finance_status}
                    onValueChange={(v) => updateApplicant({ finance_status: v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="finance-status-select">
                      <SelectValue placeholder="Select status..." />
                    </SelectTrigger>
                    <SelectContent>
                      {FINANCE_STATUSES.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Finance Status Notes</Label>
                  <Textarea
                    placeholder="Notes about finance..."
                    value={applicant.finance_status_notes || ""}
                    onChange={(e) => updateApplicant({ finance_status_notes: e.target.value })}
                    className="mt-1"
                    rows={2}
                    data-testid="finance-notes"
                  />
                </div>
              </div>

              {/* Chain Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Chain Status</Label>
                  <Select
                    value={applicant.chain_status}
                    onValueChange={(v) => updateApplicant({ chain_status: v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="chain-status-select">
                      <SelectValue placeholder="Select status..." />
                    </SelectTrigger>
                    <SelectContent>
                      {CHAIN_STATUSES.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Chain Status Notes</Label>
                  <Textarea
                    placeholder="Notes about chain..."
                    value={applicant.chain_status_notes || ""}
                    onChange={(e) => updateApplicant({ chain_status_notes: e.target.value })}
                    className="mt-1"
                    rows={2}
                    data-testid="chain-notes"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Marketing Consents (GDPR) */}
      {showMarketingConsents && (
        <div className="space-y-3 border-t pt-4">
          <div 
            className="flex items-center gap-2 cursor-pointer p-2 hover:bg-slate-50 rounded"
            onClick={() => toggleSection('marketing')}
          >
            <Shield className="w-4 h-4" />
            <Label className="text-base font-medium cursor-pointer">Marketing Consent (GDPR)</Label>
            {expandedSections.marketing ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
          
          {expandedSections.marketing && (
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-700">
                Under GDPR, explicit consent is required before sending property details, marketing info, or sharing data with third parties.
              </p>
              
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={applicant.consent_property_email}
                    onCheckedChange={(checked) => updateApplicant({ consent_property_email: checked })}
                  />
                  <span className="text-sm">Property details via email?</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={applicant.consent_property_sms}
                    onCheckedChange={(checked) => updateApplicant({ consent_property_sms: checked })}
                  />
                  <span className="text-sm">Property details via SMS?</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={applicant.consent_marketing_email}
                    onCheckedChange={(checked) => updateApplicant({ consent_marketing_email: checked })}
                  />
                  <span className="text-sm">Other marketing via email?</span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Consent Method</Label>
                  <Select
                    value={applicant.consent_method}
                    onValueChange={(v) => updateApplicant({ consent_method: v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="consent-method-select">
                      <SelectValue placeholder="How was consent obtained?" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONSENT_METHODS.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Consent Notes</Label>
                  <Textarea
                    placeholder="Additional consent details..."
                    value={applicant.consent_notes || ""}
                    onChange={(e) => updateApplicant({ consent_notes: e.target.value })}
                    className="mt-1"
                    rows={2}
                    data-testid="consent-notes"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reminder Exclusions */}
      {showReminderExclusions && (
        <div className="space-y-3 border-t pt-4">
          <div 
            className="flex items-center gap-2 cursor-pointer p-2 hover:bg-slate-50 rounded"
            onClick={() => toggleSection('reminders')}
          >
            <BellOff className="w-4 h-4" />
            <Label className="text-base font-medium cursor-pointer">Reminder Exclusions</Label>
            {expandedSections.reminders ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
          
          {expandedSections.reminders && (
            <div className="space-y-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-700">
                Tick to exclude from automated reminders.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs mb-2 block">Exclude from viewing reminders?</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={applicant.exclude_viewing_email}
                        onCheckedChange={(checked) => updateApplicant({ exclude_viewing_email: checked })}
                      />
                      <span className="text-sm">Email</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={applicant.exclude_viewing_sms}
                        onCheckedChange={(checked) => updateApplicant({ exclude_viewing_sms: checked })}
                      />
                      <span className="text-sm">SMS</span>
                    </label>
                  </div>
                </div>
                <div>
                  <Label className="text-xs mb-2 block">Exclude from appointment reminders?</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={applicant.exclude_appointment_email}
                        onCheckedChange={(checked) => updateApplicant({ exclude_appointment_email: checked })}
                      />
                      <span className="text-sm">Email</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={applicant.exclude_appointment_sms}
                        onCheckedChange={(checked) => updateApplicant({ exclude_appointment_sms: checked })}
                      />
                      <span className="text-sm">SMS</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Notes */}
      {!compact && (
        <div className="border-t pt-4">
          <Label className="text-xs">Notes</Label>
          <Textarea
            placeholder="Any additional notes..."
            value={applicant.notes || ""}
            onChange={(e) => updateApplicant({ notes: e.target.value })}
            className="mt-1"
            rows={2}
            data-testid="applicant-notes"
          />
        </div>
      )}
    </div>
  );
}

export default ApplicantForm;
